/*
 * JSON editing rules for <vitesse-value-editor> — the pure half.
 *
 * The value being edited is often a whole broker message or a struct read off a
 * customer's PLC, and it is written straight back to that system. So the rules
 * that decide what gets sent live here, apart from the widget, where they can be
 * proved without a DOM.
 *
 * Everything below is a pure function over text and a caret. Nothing here
 * touches an element, and nothing here writes.
 */

/** Two spaces, matching what JSON.stringify(v, null, 2) emits — so text that has
 *  been through `pretty` and text a person typed indent the same way. */
export const INDENT = '  ';

/** Where a parse failed, in terms a person can act on.
 *
 *  `line`/`column` are OPTIONAL and absent means "we could not tell". That
 *  matters: the gutter highlights the line it is given, and pointing at the
 *  wrong line is worse than pointing at none — it sends someone to inspect
 *  correct text. */
export interface JsonError {
  message: string;
  line?: number; // 1-based
  column?: number; // 1-based
}

/** Parse failure for `text`, or null when it parses.
 *
 *  Empty text is NOT an error here: an empty box is "no value yet", and
 *  reporting it as malformed while someone is still clearing the field to retype
 *  is noise. Whether empty may be SAVED is the caller's rule, not the parser's. */
export function jsonError(text: string): JsonError | null {
  if (text.trim() === '') return null;
  try {
    JSON.parse(text);
    return null;
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Invalid JSON';
    return { message, ...locate(text, message) };
  }
}

/*
 * Finding WHERE, across engines that disagree about telling us.
 *
 * V8 is inconsistent in a way that matters: a structural mistake gets
 * "...at position 7 (line 1 column 8)", but the single most common typo — a
 * trailing comma — gets "Unexpected token ',', ..."…" is not valid JSON" with no
 * position anywhere in it. Trusting the message alone therefore locates the
 * errors people rarely make and gives up on the one they make constantly.
 *
 * So: take the engine's own answer when it gives one (it is authoritative, and
 * free), and otherwise scan for the offset ourselves. If both fail, say nothing
 * rather than guess.
 */
function locate(text: string, message: string): { line?: number; column?: number } {
  const lc = /line (\d+) column (\d+)/.exec(message);
  if (lc) return { line: Number(lc[1]), column: Number(lc[2]) };

  const pos = /position (\d+)/.exec(message);
  if (pos) return offsetToLineCol(text, Number(pos[1]));

  // "Unexpected end of JSON input" — the text simply stops early, so the place
  // to look is the end of it.
  if (/end of (JSON )?input/i.test(message)) return offsetToLineCol(text, text.length);

  const scanned = firstBadOffset(text);
  return scanned < 0 ? {} : offsetToLineCol(text, scanned);
}

function offsetToLineCol(text: string, offset: number): { line: number; column: number } {
  const upTo = text.slice(0, Math.max(0, Math.min(offset, text.length)));
  const lines = upTo.split('\n');
  return { line: lines.length, column: lines[lines.length - 1].length + 1 };
}

/*
 * A minimal JSON validator that reports the offset it stops at.
 *
 * It exists ONLY to answer "where", never "is this valid" — JSON.parse has
 * already answered that, and this runs only after it has said no. It accepts
 * the JSON grammar and returns the first offset that does not fit; -1 if it
 * somehow finds nothing wrong, in which case the caller reports no position
 * rather than inventing one.
 */
function firstBadOffset(text: string): number {
  let i = 0;
  const ws = () => {
    while (i < text.length && (text[i] === ' ' || text[i] === '\n' || text[i] === '\t' || text[i] === '\r')) i++;
  };
  const fail = { at: -1 };
  const bad = (at: number): false => {
    if (fail.at < 0) fail.at = at;
    return false;
  };

  const str = (): boolean => {
    if (text[i] !== '"') return bad(i);
    i++;
    while (i < text.length) {
      const c = text[i];
      if (c === '\\') {
        i += 2;
        continue;
      }
      if (c === '"') {
        i++;
        return true;
      }
      if (c === '\n') return bad(i); // a raw newline inside a string is invalid JSON
      i++;
    }
    return bad(text.length);
  };

  const num = (): boolean => {
    const m = /^-?(0|[1-9]\d*)(\.\d+)?([eE][+-]?\d+)?/.exec(text.slice(i));
    if (!m || m[0] === '') return bad(i);
    i += m[0].length;
    return true;
  };

  const lit = (word: string): boolean => {
    if (text.startsWith(word, i)) {
      i += word.length;
      return true;
    }
    return bad(i);
  };

  const value = (depth: number): boolean => {
    if (depth > 200) return bad(i); // runaway nesting: stop rather than blow the stack
    ws();
    if (i >= text.length) return bad(text.length);
    const c = text[i];
    if (c === '"') return str();
    if (c === '{') return obj(depth);
    if (c === '[') return arr(depth);
    if (c === 't') return lit('true');
    if (c === 'f') return lit('false');
    if (c === 'n') return lit('null');
    if (c === '-' || (c >= '0' && c <= '9')) return num();
    return bad(i);
  };

  const obj = (depth: number): boolean => {
    i++; // {
    ws();
    if (text[i] === '}') {
      i++;
      return true;
    }
    for (;;) {
      ws();
      if (!str()) return false; // a key must be a double-quoted string
      ws();
      if (text[i] !== ':') return bad(i);
      i++;
      if (!value(depth + 1)) return false;
      ws();
      if (text[i] === ',') {
        i++;
        ws();
        // The trailing comma: `,` then `}`. The offending character is the
        // BRACE, which is what the engine names too.
        if (text[i] === '}') return bad(i);
        continue;
      }
      if (text[i] === '}') {
        i++;
        return true;
      }
      return bad(i);
    }
  };

  const arr = (depth: number): boolean => {
    i++; // [
    ws();
    if (text[i] === ']') {
      i++;
      return true;
    }
    for (;;) {
      if (!value(depth + 1)) return false;
      ws();
      if (text[i] === ',') {
        i++;
        ws();
        if (text[i] === ']') return bad(i);
        continue;
      }
      if (text[i] === ']') {
        i++;
        return true;
      }
      return bad(i);
    }
  };

  if (!value(0)) return fail.at;
  ws();
  if (i < text.length) return i; // junk after the document
  return fail.at;
}

/** Re-indent `text`. Returns the original untouched when it does not parse —
 *  formatting is a convenience and must never be a way to lose an edit someone
 *  is midway through. Callers show `jsonError` instead. */
export function pretty(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, INDENT.length);
  } catch {
    return text;
  }
}

/** Collapse to one line. The inverse of `pretty`, for a value going somewhere
 *  that charges by the byte. Same refusal on malformed input. */
export function compact(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text));
  } catch {
    return text;
  }
}

/** Whether `text` is already exactly what `pretty` would produce — so a Format
 *  control can be disabled rather than offered as a no-op. */
export function isPretty(text: string): boolean {
  return jsonError(text) === null && text.trim() !== '' && pretty(text) === text;
}

/** A text edit: the new text and where the caret lands. */
export interface Edit {
  text: string;
  start: number;
  end: number;
}

/** The indentation of the line containing `offset`. */
function indentAt(text: string, offset: number): string {
  const lineStart = text.lastIndexOf('\n', offset - 1) + 1;
  const m = /^[ \t]*/.exec(text.slice(lineStart, offset));
  return m ? m[0] : '';
}

/**
 * Enter: keep the current indentation, and go one level deeper after an opening
 * bracket. When the closing bracket sits immediately after the caret it is
 * pushed to its own line, so typing `{` then Enter gives the shape you meant:
 *
 *     {            {
 *       |     →      |
 *     }            }
 *
 * Without this, every nested level is typed by hand, which is why a
 * plain textarea feels nothing like an editor.
 */
export function newline(text: string, start: number, end: number): Edit {
  const before = text.slice(0, start);
  const after = text.slice(end);
  const indent = indentAt(text, start);
  const opensBlock = /[[{]\s*$/.test(before);
  const closesNext = /^\s*[\]}]/.test(after);

  if (opensBlock && closesNext) {
    const inner = indent + INDENT;
    const insert = '\n' + inner + '\n' + indent;
    return { text: before + insert + after, start: start + 1 + inner.length, end: start + 1 + inner.length };
  }
  const next = opensBlock ? indent + INDENT : indent;
  const caret = start + 1 + next.length;
  return { text: before + '\n' + next + after, start: caret, end: caret };
}

/**
 * Tab: indent, never leave the field.
 *
 * A textarea's native Tab moves focus, which in a modal means landing on Save —
 * so the key most used for structure was the key that took you to the button
 * that writes to a PLC. With a selection spanning lines it indents each of them;
 * `back` (Shift+Tab) removes one level.
 */
export function tab(text: string, start: number, end: number, back = false): Edit {
  const lineStart = text.lastIndexOf('\n', start - 1) + 1;
  const spansLines = text.slice(start, end).includes('\n');

  if (!spansLines && !back) {
    return { text: text.slice(0, start) + INDENT + text.slice(end), start: start + INDENT.length, end: start + INDENT.length };
  }

  const blockEnd = end + (text.slice(end).match(/^[^\n]*/)?.[0].length ?? 0);
  const block = text.slice(lineStart, blockEnd);
  const lines = block.split('\n');
  let firstDelta = 0;
  let total = 0;
  const shifted = lines.map((line, i) => {
    if (back) {
      const removed = /^ {1,2}|^\t/.exec(line)?.[0].length ?? 0;
      if (i === 0) firstDelta = -removed;
      total -= removed;
      return line.slice(removed);
    }
    if (i === 0) firstDelta = INDENT.length;
    total += INDENT.length;
    return line === '' ? line : INDENT + line;
  });
  const out = text.slice(0, lineStart) + shifted.join('\n') + text.slice(blockEnd);
  return { text: out, start: Math.max(lineStart, start + firstDelta), end: Math.max(lineStart, end + total) };
}

/** Bracket and quote pairs that auto-close. */
const PAIRS: Record<string, string> = { '{': '}', '[': ']', '"': '"' };

/**
 * Typing an opener inserts its closer and leaves the caret between them; with
 * text selected it WRAPS the selection instead of replacing it (losing a
 * selection to a stray keystroke is the expensive version of this feature).
 * Returns null when the character is not one we pair, so the caller lets the
 * browser handle it.
 */
export function autoClose(ch: string, text: string, start: number, end: number): Edit | null {
  const close = PAIRS[ch];
  if (!close) return null;
  const selected = text.slice(start, end);
  if (selected) {
    return { text: text.slice(0, start) + ch + selected + close + text.slice(end), start: start + 1, end: end + 1 };
  }
  // A quote right before a word would be an opening quote for text already
  // there; pairing it would strand a quote mid-token.
  if (ch === '"' && /^[\w"]/.test(text.slice(end, end + 1))) return null;
  return { text: text.slice(0, start) + ch + close + text.slice(end), start: start + 1, end: start + 1 };
}

/** Typing a closer where one was auto-inserted steps over it rather than
 *  doubling it — the other half of `autoClose`. */
export function skipClose(ch: string, text: string, start: number, end: number): Edit | null {
  if (start !== end) return null;
  if (!Object.values(PAIRS).includes(ch)) return null;
  if (text.slice(start, start + 1) !== ch) return null;
  return { text, start: start + 1, end: start + 1 };
}

/** How many lines the gutter must number. Always at least 1, so an empty editor
 *  still shows a line 1 rather than a blank strip. */
export function lineCount(text: string): number {
  return Math.max(1, text.split('\n').length);
}
